import {
  Button,
  Tile,
  TileContent,
  TileMedia,
  TileText,
  TileTitle,
} from '@design-system';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { renderShopifyArtistName } from '@utils/artists';
import { productUrl } from '@utils/productUrl';
import {
  shopifyAdminIdToGraphqlId,
  shopifyGraphqlIdToAdminId,
} from '@utils/shopifyIds';
import { AnimatePresence, motion } from 'motion/react';
import Link from 'next/link';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSetCheckoutLink } from '../../features/checkout/hooks/useSetCheckoutLink';
import { useIsPreOrder } from '../../hooks/useIsPreOrder';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useProducts } from '../../hooks/useProducts';
import {
  GeneratedGetDrawByProductIDsQuery,
  useGetDrawByProductIDsQuery,
} from '../../types/generated';
import { ImageWithProductsStoryblok } from '../../types/generated-storyblok';
import { ClickOutside } from '../ClickOutside';
import ProductDate from '../Containers/ProductDate';
import { getProductTileStatus } from '../ProductTile/utils/status';
import { Image } from './Image';

const ProductRow = ({
  draw,
  onClickProduct,
  product,
}: {
  product: Product;
  draw?: GeneratedGetDrawByProductIDsQuery['getDrawByProductIDs'][number];
  onClickProduct: () => void;
}) => {
  const { t } = useTranslation('common');
  const isPreOrder = useIsPreOrder(draw);
  const tileStatus = getProductTileStatus(product, isPreOrder, draw);
  const href = productUrl(product.full_slug!, product.productType);

  return (
    <Tile
      key={product.id}
      className="group py-3 first:pt-0 last:pb-0"
      direction="row"
    >
      <TileMedia className="size-16 shrink-0">
        <Image
          alt={product.alt || product.title}
          src={product.image}
          hoverEffect
          width={128}
        />
      </TileMedia>
      <TileContent className="w-full" direction="column">
        <TileTitle>
          <Link
            href={href}
            target="_blank"
            onClick={onClickProduct}
            className="after:absolute after:inset-0 after:block after:content-['']"
          >
            {renderShopifyArtistName(product.artist)}
          </Link>
        </TileTitle>
        <TileText>{product.title}</TileText>
        {tileStatus ? (
          <TileText variant={tileStatus.isActive ? 'active' : 'muted'}>
            {tileStatus.variant === 'text' ? (
              t(tileStatus.key)
            ) : (
              <ProductDate
                date={tileStatus.date}
                keyCountdownDate={`${tileStatus.key}.date`}
                keyCountdownDays={`${tileStatus.key}.days`}
                keyCountdownTime={`${tileStatus.key}.time`}
                uniqueDateFormat="dd MMMM"
              />
            )}
          </TileText>
        ) : null}
      </TileContent>
    </Tile>
  );
};

const getBlokProductIds = (
  products: ImageWithProductsStoryblok['products'],
) => {
  return products.map((p) => shopifyAdminIdToGraphqlId(p.id || ''));
};

export const ImageWithProducts = ({
  blok,
}: {
  blok: ImageWithProductsStoryblok;
}) => {
  const { t } = useTranslation('common');
  const [emitter] = useAnalyticsEmitter();
  const isBreakpointLg = useMediaQuery('lg');
  const [isOpen, setIsOpen] = useState(false);

  const products = blok.products;
  const multipleProducts = products.length > 1;

  const { products: shopifyProducts } = useProducts(
    getBlokProductIds(products),
  );
  const productIDs = shopifyProducts.map((p) =>
    shopifyGraphqlIdToAdminId(p.id),
  );
  const isSoldOut =
    shopifyProducts.filter((p) => p.availability === 'Sold out').length > 0;

  const draws = useGetDrawByProductIDsQuery({
    variables: { productIDs },
    skip: productIDs.length === 0,
  });

  const checkout = useSetCheckoutLink(productIDs, draws);

  const productSetUrl =
    shopifyProducts.length > 0
      ? `${productUrl(
          shopifyProducts[0].full_slug!,
          shopifyProducts[0].productType,
        )}?collectionSet=${productIDs.join(',')}`
      : '';
  const buySetLink = checkout.loading
    ? ({ disabled: true } as const)
    : ({
        as: 'a',
        href: checkout.link ?? productSetUrl,
        target: checkout.link ? undefined : '_blank',
      } as const);

  return (
    <ClickOutside className="w-full" onClickOutside={() => setIsOpen(false)}>
      <figure className="relative aspect-[3/4] w-full overflow-hidden rounded-sm lg:aspect-auto">
        <Image
          alt={blok.image?.alt || blok.image.filename}
          className="h-full w-full overflow-hidden rounded-sm"
          size={{
            sm: { span: 12, ratio: isBreakpointLg ? 'intrinsic' : '3/4' },
            md: { span: 12, ratio: isBreakpointLg ? 'intrinsic' : '3/4' },
            lg: { span: 12, ratio: isBreakpointLg ? 'intrinsic' : '3/4' },
          }}
          src={blok.image.filename}
        />
        {shopifyProducts.length > 0 ? (
          <AnimatePresence mode="popLayout">
            <motion.figcaption
              key="productList"
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
              }}
              initial={{
                opacity: 0,
              }}
              className="absolute inset-0 flex flex-col items-center justify-end"
            >
              <AnimatePresence mode="popLayout">
                {isOpen ? (
                  <motion.div
                    key="open"
                    animate={{
                      opacity: 1,
                    }}
                    exit={{
                      opacity: 0,
                    }}
                    initial={{
                      opacity: 0,
                    }}
                    className="flex w-full flex-1 flex-col items-center justify-end gap-2 overflow-hidden p-4 md:p-6"
                  >
                    <motion.div
                      animate={{
                        opacity: 1,
                        y: 0,
                      }}
                      exit={{
                        opacity: 0,
                        y: 96,
                      }}
                      initial={{
                        opacity: 0,
                        y: 96,
                      }}
                      className="flex w-full max-w-md flex-col gap-4 overflow-hidden rounded-md bg-white p-4"
                    >
                      <div className="flex flex-1 flex-col divide-y divide-divider/20 overflow-auto no-scrollbar">
                        {shopifyProducts.map((product) => {
                          const draw = draws.data?.getDrawByProductIDs?.find(
                            (d) =>
                              d.productId ===
                              shopifyGraphqlIdToAdminId(product.id),
                          );
                          return (
                            <ProductRow
                              key={product.id}
                              product={product}
                              draw={draw}
                              onClickProduct={() => {
                                emitter('', 'product clicked', {
                                  button_location: 'imageWithProducts',
                                });
                              }}
                            />
                          );
                        })}
                      </div>

                      {!isSoldOut ? (
                        <Button
                          label={
                            multipleProducts
                              ? t('imageWithProducts.buy.others')
                              : t('imageWithProducts.buy.one')
                          }
                          size="md"
                          fullWidth
                          {...buySetLink}
                          onClick={() => {
                            emitter('', 'button clicked', {
                              label: multipleProducts
                                ? t('imageWithProducts.buy.others')
                                : t('imageWithProducts.buy.one'),
                              button_location: 'imageWithProducts',
                              productIDs,
                              isSoldOut,
                            });
                          }}
                        />
                      ) : null}
                    </motion.div>

                    <motion.div
                      animate={{
                        opacity: 1,
                      }}
                      exit={{
                        opacity: 0,
                      }}
                      initial={{
                        opacity: 0,
                      }}
                      className="flex shrink-0 gap-2 rounded-full bg-white p-2"
                    >
                      <Button
                        label={t('imageWithProducts.close')}
                        variant="transparent"
                        icon="ic_close"
                        size="sm"
                        hideLabel
                        onClick={() => setIsOpen(false)}
                      />
                    </motion.div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="default"
                    animate={{
                      opacity: 1,
                    }}
                    exit={{
                      opacity: 0,
                    }}
                    initial={{
                      opacity: 0,
                    }}
                    className="flex w-full flex-col items-center justify-center gap-2 p-6"
                  >
                    <motion.div
                      animate={{
                        opacity: 1,
                      }}
                      exit={{
                        opacity: 0,
                      }}
                      initial={{
                        opacity: 0,
                      }}
                      className="flex items-center justify-center divide-x divide-divider/20 rounded-md bg-white py-2"
                    >
                      <span className="px-2">
                        <Button
                          label={
                            multipleProducts
                              ? t('imageWithProducts.see.others')
                              : t('imageWithProducts.see.one')
                          }
                          variant={isSoldOut ? 'transparent' : 'secondary'}
                          size="sm"
                          onClick={() => {
                            emitter('', 'button clicked', {
                              label: multipleProducts
                                ? t('imageWithProducts.see.others')
                                : t('imageWithProducts.see.one'),
                              button_location: 'imageWithProducts',
                              productIDs,
                              isSoldOut,
                            });
                            setIsOpen(true);
                          }}
                        />
                      </span>
                      {!isSoldOut ? (
                        <span className="px-2">
                          <Button
                            label={
                              multipleProducts
                                ? t('imageWithProducts.buy.others')
                                : t('imageWithProducts.buy.one')
                            }
                            variant="primary"
                            onClick={() => {
                              emitter('', 'button clicked', {
                                label: multipleProducts
                                  ? t('imageWithProducts.buy.others')
                                  : t('imageWithProducts.buy.one'),
                                button_location: 'imageWithProducts',
                                productIDs,
                                isSoldOut,
                              });
                            }}
                            size="sm"
                            {...buySetLink}
                          />
                        </span>
                      ) : null}
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.figcaption>
          </AnimatePresence>
        ) : null}
      </figure>
    </ClickOutside>
  );
};
